import {
    Fragment,
    Ref,
    useEffect,
    useState,
} from 'react';
import { ErrorMessage, Notification } from '@honeycomb-npm/honeycomb-react';
import { sanitizeUrl } from '@braintree/sanitize-url';

import { createSelector } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { RootState } from '../../../app/storeTypes';
import { BladeInput } from './BladeInput';
import { BladeDropdown } from './BladeDropdown';
import { BladeRadio } from './BladeRadio';
import {
    selectGenericBladeCancelApiErrorMessage,
    selectGenericBladeFetchApiErrorMessage,
    selectGenericBladeFetchApiStatus,
    selectGenericBladeFetchUrl,
    selectGenericBladeParameters,
    selectGenericBladeSubmitApiErrorMessage,
    selectGenericBladeSubmittedPayload,
    selectGenericBladeType,
    selectTimeDurationYears,
} from '../genericBladeSelectors';
import {
    genericBladeCancel,
    genericBladeFetch,
    genericBladeSubmit,
    resetGenericBladeStatus,
} from '../genericBladeSlice';
import {
    CancelPayload,
    GenericBladeDynamicContentInfo,
    GenericBladeParsedDataType,
    GenericBladeType,
    GenericFormAEM,
    NodePayload,
    SubmittedPayload,
} from '../genericBladeTypes';
import { DynamicContent } from '../../../utils/DynamicContent';
import { SpinnerWrapper } from '../../../sharedComponents/SpinnerWrapper';
import {
    ApiStatus,
    BladeActionButtonT,
    BladeBaseProps,
} from '../../../types';
import BladeHeading from '../../../sharedComponents/BladeHeading';
import { BladeSelectiveInput } from './BladeSelectiveInput';
import { getValidationMessage } from '../../../utils/contentUtil';
import ConfirmSteps from './ConfirmSteps';
import Callout from '../../../sharedComponents/Callout.styled';
import TimeDuration from './TimeDuration';
import GenericLegend from './GenericLegend.styled';
import { useIsFocusedHeading } from '../../../utils/useIsFocusedHeading';
import mapGenericBladeParsedApiData from './mapGenericBladeParsedApiData';
import mapSelectiveInputDropdownOptions from './mapSelectiveInputDropdownOptions';
import { ModifiedButtonAction } from './styles/GenericBlade.styled';

enum YesNoSelection {
    Yes = 'Y',
    No = 'N'
}

type YesNoValue = 'Y' | 'N' | null;

interface Props extends BladeBaseProps {
    id: string;
    bladeHeadingIcon?: string;
    highlightCallout?: boolean;
    confirmButtonVariant?: 'default' | 'emphasis' | 'subtle';
    cancelButtonVariant?: 'default' | 'emphasis' | 'subtle';
    confirmButtonRef?: Ref<BladeActionButtonT>;
    cancelButtonRef?: Ref<BladeActionButtonT>;
    showHelptextIcon?: boolean;
}

export const GenericBlade = ({
    id,
    bladeHeadingIcon,
    highlightCallout,
    confirmButtonVariant,
    cancelButtonVariant,
    isFocusedBlade,
    isMainHeading = false,
    disableReset = false,
    confirmButtonRef,
    cancelButtonRef,
    showHelptextIcon,
}: Props) => {
    const genericBladeInfo = useAppSelector(createSelector(
        [(state: RootState) => state.genericBlade?.[id]],
        bladeInfo => bladeInfo,
    ));

    if (!genericBladeInfo) {
        // Should not run anything after this line if blade data does not exist
        return null;
    }

    const dispatch = useAppDispatch();

    const type = useAppSelector((state) => selectGenericBladeType(state, id));

    const {
        heading,
        description,
        callouts,
        calloutParams,
        notificationHeading,
        notificationText,
        stepsHeading,
        steps,
        stepsDescription,
        stepsFooter,
        confirmButtonText,
        cancelButtonText,
        cancelButtonLink,
        confirmButtonLink,
        validations,
        hiddenInputs,
        img,
        ...restContent
    } = genericBladeInfo.content;

    const [isDispatched, setIsDispatched] = useState<boolean>(false);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<GenericBladeParsedDataType>({});

    const isYesNoBlade = confirmButtonText === 'Yes' && cancelButtonText === 'No';

    const [isYesNoClicked, setIsYesNoClicked] = useState<YesNoValue>(null);
    const isYesClicked = isYesNoClicked === YesNoSelection.Yes;
    const isNoClicked = isYesNoClicked === YesNoSelection.No;

    const [errors, setErrorMessages] = useState<Record<string, string>>({});

    const dynamicContent = restContent as GenericBladeDynamicContentInfo;

    const hasAnyForm = !!Object.keys(dynamicContent).length;

    // Warp whole blade with fieldset and warp blade heading with legend
    // When any form field or multiple action buttons are available
    // So content can be linked with heading context from the accessibility prospective
    const useBladeFieldset = !!cancelButtonText ||  hasAnyForm;

    const WrapperFieldset = useBladeFieldset ? 'fieldset' : Fragment;

    const FieldsetProps = useBladeFieldset ? {
        id: `${id}-fieldset`,
    } : null;

    const params = useAppSelector((state) => selectGenericBladeParameters(state, id));

    const apiFetchUrl = useAppSelector((state: RootState) =>
        selectGenericBladeFetchUrl(state, id),
    );

    const genericBladeParsedApiData = mapGenericBladeParsedApiData({
        dynamicContent: dynamicContent,
        api: genericBladeInfo.api,
    });

    const dropdownOptions = mapSelectiveInputDropdownOptions({ dynamicContent: dynamicContent });

    const submittedPayload = useAppSelector((state) =>
        selectGenericBladeSubmittedPayload(state, id),
    );

    const apiFetchErrorMessage = useAppSelector((state: RootState) =>
        selectGenericBladeFetchApiErrorMessage(state, id),
    );

    const apiSubmitErrorMessage = useAppSelector((state: RootState) =>
        selectGenericBladeSubmitApiErrorMessage(state, id),
    );

    const apiCancelErrorMessage = useAppSelector((state: RootState) =>
        selectGenericBladeCancelApiErrorMessage(state, id),
    );

    const genericBladeFetchApiStatus = useAppSelector((state) =>
        selectGenericBladeFetchApiStatus(state, id),
    );

    const isFocusedHeading = useIsFocusedHeading({
        isReady: isLoaded,
        isFocused: isFocusedBlade,
    });

    const years = useAppSelector((state) => selectTimeDurationYears(state, id));

    const mapAemData = (bladeType: GenericBladeType, aemData: GenericBladeDynamicContentInfo) => {
        if (bladeType === GenericBladeType.input
            || bladeType === GenericBladeType.dropdown
            || type === GenericBladeType.timeDuration
            || bladeType === GenericBladeType.radio
        ) {
            return Object.entries(aemData).reduce(
                (result, [key, value]) => {
                    if (typeof value === 'object') {
                        return {
                            ...result,
                            [key]: value as GenericFormAEM,
                        };
                    }
                    else {
                        return result;
                    }

                },
                {} as Record<string, GenericFormAEM>,
            );
        }

        return {} as Record<string, GenericFormAEM>;
    };

    const formAem = mapAemData(type, dynamicContent);

    const handleChange = (node: string, newValue: NodePayload | null | undefined) => {
        setFormValues((formValue) => ({
            ...formValue,
            [node]: {
                ...formValue[node],
                value: newValue,
            },
        }));
    };

    const validation = (submittedPayloadObj: SubmittedPayload) => {
        setErrorMessages({});
        const errorMessages: Record<string, string> = {};

        if (!validations?.hardValidations) {
            return true;
        }

        const { hardValidations } = validations;

        Object.entries(hardValidations).map(([node]) => {
            const submittedValue = submittedPayloadObj[node];
            let updatedNode = node;

            if (type === GenericBladeType.selectiveInput) {
                if (Object.keys(submittedPayloadObj).length === 0) {
                    errorMessages[dropdownOptions.key] = hardValidations[dropdownOptions.key]?.defaultErrorMessage ?? '';

                    return;
                }
                const dropdownOption = dropdownOptions.options.find(
                    option => option.value.includes((submittedPayloadObj[dropdownOptions.key] ?? '').toString()),
                );
                const values = dropdownOption?.value?.split(':');

                if (!values?.includes(node)) {
                    return;
                }

                updatedNode = values.pop() ?? node;

                if ((dynamicContent[updatedNode] as GenericFormAEM)?.hideInput?.includes(values[0])) {
                    return;
                }
            }

            // Radio blade validation: when on confirm button is clicked
            else if (type === GenericBladeType.radio) {
                // Submitted value was empty or invalid
                if (!submittedValue) {
                    errorMessages[node] = '';

                    return;
                }
                delete errorMessages[node];

            }

            const errorMessage = getValidationMessage(hardValidations[updatedNode], submittedValue as string);

            if (errorMessage && !(type === GenericBladeType.timeDuration && updatedNode === 'months' && (years.length -1).toString() === submittedPayloadObj?.['years'])) {
                errorMessages[updatedNode] = errorMessage;
            }
        });

        setErrorMessages({ ...errorMessages });
        if (Object.keys(errorMessages).length > 0) {
            return false;
        }

        return true;
    };

    const handleSubmit = (formValuesObj: GenericBladeParsedDataType) => {
        const submittedPayloadObj = Object.entries(formValuesObj).reduce(
            (result, [key, value]) => ({
                ...result,
                [key]: value.value ?? null,
            }),
            {} as SubmittedPayload,
        );
        
        if (isYesNoBlade) {
            setIsYesNoClicked('Y');
        }
        
        if (type === GenericBladeType.confirm) {
            dispatch(genericBladeSubmit({
                id,
                submittedPayload: submittedPayloadObj,
            }));
        } else if (validation(submittedPayloadObj)) {

            if (type === GenericBladeType.selectiveInput) {
                const updatedPayload = Object.entries(submittedPayloadObj).reduce(
                    (result, [key, value]) => ({
                        ...result,
                        [key.split(':')[1] ?? key]: value,
                    }),
                    {} as SubmittedPayload,
                );

                dispatch(genericBladeSubmit({
                    id,
                    submittedPayload: updatedPayload,
                }));
            } else {
                dispatch(genericBladeSubmit({
                    id,
                    submittedPayload: submittedPayloadObj,
                }));
            }
        }
    };

    const handleCancel = (formValuesObj: GenericBladeParsedDataType) => {
        const cancelPayloadObj = Object.entries(formValuesObj).reduce(
            (result, [key, value]) => ({
                ...result,
                [key]: value.value ?? null,
            }),
            {} as CancelPayload,
        );

        if (isYesNoBlade) {
            setIsYesNoClicked('N');
        }

        dispatch(genericBladeCancel({
            id,
            cancelPayload: cancelPayloadObj,
        }));
    };

    const handleRedirect = (url: string) => {
        window.location.replace(sanitizeUrl(url));
    };

    useEffect(() => {
        if (!apiFetchUrl || submittedPayload) {
            setIsLoaded(true);

            return;
        }

        if (
            !isDispatched &&
            (genericBladeFetchApiStatus === ApiStatus.Idle ||
                genericBladeFetchApiStatus === ApiStatus.Rejected)
        ) {
            dispatch(genericBladeFetch(id));
            setIsDispatched(true);
        }

        if (
            isDispatched &&
            (genericBladeFetchApiStatus === ApiStatus.Fulfilled ||
                genericBladeFetchApiStatus === ApiStatus.Rejected)
        ) {
            setFormValues({
                ...formValues,
                ...genericBladeParsedApiData,
            });
            setIsLoaded(true);
        }
    }, [apiFetchUrl, submittedPayload, isDispatched, genericBladeFetchApiStatus]);

    useEffect(() => () => {
        if (!disableReset) {
            dispatch(resetGenericBladeStatus(id));
        }
    }, []);

    return (
        <SpinnerWrapper isLoaded={isLoaded} id={id}>
            <WrapperFieldset {...FieldsetProps}>
                {img && <img className="mw-100" src={img} alt="" />}
                {useBladeFieldset ?
                    <GenericLegend>
                        <BladeHeading
                            id={`${id}-main-heading`}
                            bladeHeadingIcon={bladeHeadingIcon}
                            isFocused={isFocusedHeading}
                            isMainHeading={isMainHeading}
                        >
                            <DynamicContent
                                params={params?.heading}
                                content={heading}
                                id={`${id}-main-heading`} />
                        </BladeHeading>
                    </GenericLegend> :
                    <BladeHeading
                        id={`${id}-main-heading`}
                        bladeHeadingIcon={bladeHeadingIcon}
                        isFocused={isFocusedHeading}
                        isMainHeading={isMainHeading}
                    >
                        <DynamicContent
                            params={params?.heading}
                            content={heading}
                            id={`${id}-main-heading`} />
                    </BladeHeading>
                }

                {callouts && (
                    <dl className="my-2" id={`${id}-callout-list`}>
                        {callouts.map((callout, index) => (
                            <Callout highlightCallout={highlightCallout} key="callout">
                                <dt id={`${id}-callout-heading-${index}`} className="d-inline">{`${callout} `}</dt>
                                <dd id={`${id}-callout-description-${index}`} className="d-inline">{calloutParams?.[index]}</dd>
                            </Callout>
                        ))}
                    </dl>
                )}

                <DynamicContent
                    isParagraph
                    content={description}
                    id={`${id}-main-description`}
                    params={params?.description}
                    className="mb-4 mb-md-3"
                />
                {notificationText && (
                    <div id={`${id}-notification`}>
                        <Notification
                            animation={false}
                            className="mb-4"
                            heading={notificationHeading}
                            isOpen
                            type="info"
                            isAutoFocus={false}
                        >
                            <DynamicContent
                                id={`${id}-notification`}
                                content={notificationText}
                            />
                        </Notification>
                    </div>
                )}
                {steps && (
                    <ConfirmSteps
                        {...{
                            stepsHeading,
                            steps,
                            stepsDescription,
                            stepsFooter,
                        }}
                    />
                )}
                {type === GenericBladeType.timeDuration && (
                    <TimeDuration {...{ id, genericFormAEM: formAem, formValues, handleChange, errors }} />
                )}
                {type === GenericBladeType.input && formAem && (
                    <BladeInput
                        {...{
                            id,
                            genericFormAEM: formAem,
                            genericFormApiData: formValues,
                            validations,
                            handleChange,
                            errors,
                        }}
                    />
                )}
                {type === GenericBladeType.dropdown && formAem && (
                    <BladeDropdown
                        {...{
                            id,
                            genericFormAEM: formAem,
                            genericFormApiData: formValues,
                            handleChange,
                            errors,
                        }}
                    />
                )}
                {type === GenericBladeType.radio && formAem && (
                    <BladeRadio
                        {...{
                            id,
                            genericFormAEM: formAem,
                            genericFormApiData: formValues,
                            validations,
                            hiddenInputs,
                            handleChange,
                            errors,
                            showHelptextIcon,
                        }}
                    />
                )}
                {type === GenericBladeType.selectiveInput && dynamicContent && (
                    <BladeSelectiveInput
                        {...{
                            id,
                            genericFormAEM: dynamicContent,
                            genericFormApiData: formValues,
                            validations,
                            handleChange,
                            errors,
                            dropdownOptions,
                        }}
                    />
                )}
                <ModifiedButtonAction
                    $isActive={isYesClicked}
                    aria-pressed={isYesClicked}
                    variant={confirmButtonVariant ?? 'default'}
                    id={`${id}-confirm-button`}
                    onClick={() => {
                        confirmButtonLink
                            ? handleRedirect(confirmButtonLink)
                            : handleSubmit(formValues);
                    }}
                    ref={confirmButtonRef}
                >
                    {confirmButtonText}
                </ModifiedButtonAction>
                {cancelButtonText && (
                    <ModifiedButtonAction
                        $isActive={isNoClicked}
                        aria-pressed={isNoClicked}
                        variant={cancelButtonVariant ?? 'subtle'}
                        id={`${id}-cancel-button`}
                        onClick={() => {
                            handleCancel(formValues);
                        }}
                        ref={cancelButtonRef}
                        {...(cancelButtonLink && {
                            href: sanitizeUrl(cancelButtonLink),
                        })}
                    >
                        {cancelButtonText}
                    </ModifiedButtonAction>
                )}
                {apiFetchErrorMessage && (
                    <ErrorMessage
                        forceVisible
                        iconType="svgfile"
                        id={`generic-blade-fetch-api-error-${id}`}
                        message={apiFetchErrorMessage}
                    />
                )}
                {apiSubmitErrorMessage && (
                    <ErrorMessage
                        forceVisible
                        iconType="svgfile"
                        id={`generic-blade-submit-api-error-${id}`}
                        message={apiSubmitErrorMessage}
                    />
                )}
                {apiCancelErrorMessage && (
                    <ErrorMessage
                        forceVisible
                        iconType="svgfile"
                        id={`generic-blade-cancel-api-error-${id}`}
                        message={apiCancelErrorMessage}
                    />
                )}
            </WrapperFieldset>
        </SpinnerWrapper>
    );
};
